import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {Services} from 'src/services/service-manager';
import {CodeType} from 'src/contracts/codeType';
import {useGrouperStore} from 'stores/grouper-store';
import { DrgAnalysisCode } from 'src/contracts/drgAnalysis';
import {
  BookViewRecord, CodebookPCSTableResponse,
  SearchViewRecord,
  SearchService, SearchHistory, Column
} from 'src/services/search-service';
import { useHistoryStore } from 'stores/history-store';
import { isEmpty } from 'src/common/arrayUtil';
import {SearchState} from 'src/contracts/encoderState';
import {useResourcesStore} from 'stores/resources-store';

const searchService = Services.service(SearchService);

export type SearchMode = 'index'|'table'|'';

const FIND_ALL = 'FindAll';

export const CodeBookDropdownOptions = [
  'Diagnosis, ICD-10-CM',
  'External Cause, ICD-10-CM',
  'Procedure, ICD-10-PCS',
];

const codeBookLookupMap = new Map<string, string> ([
  ['Diagnosis, ICD-10-CM', 'ICD10CM_DX'],
  ['External Cause, ICD-10-CM', 'ICD10CM_E'],
  ['Procedure, ICD-10-PCS', 'ICD10PCS_PR']
]);

export const trucodePartialProcedureCodeLink = /^.*<a href.*?>([A-Z0-9]+)<\/a>?/;
export const trucodeSeeMoreLink = /^.*<a href.*?><span .* data-ref-options='(.*)'.*?>(.*)<\/span><\/a>/;

export const useSearchStore = defineStore('searchStore', () => {

  const resourcesStore = useResourcesStore();

  function getSearchState(): SearchState {
    return {
      bookSelected: bookSelected.value,
      modeSelected: modeSelected.value,
      searchText: searchText.value,
      searchResults: searchResults.value,
      selectedSearchResult: selectedSearchResult.value,
      searchResultDetails: searchResultDetails.value,
      initialItemRecord:  initialItemRecord.value,
      searchTable: searchTable.value,
      pcsSectionColumn: pcsSectionColumn.value,
      selectedProcedureCode: selectedProcedureCode.value
    };
  }

  function setSearchState(searchState: SearchState): void {
    bookSelected.value = searchState.bookSelected;
    modeSelected.value = searchState.modeSelected;
    searchText.value = searchState.searchText;
    searchResults.value = searchState.searchResults;
    selectedSearchResult.value = searchState.selectedSearchResult;
    searchResultDetails.value = searchState.searchResultDetails;
    initialItemRecord.value = searchState.initialItemRecord;
    searchTable.value = searchState.searchTable;
    pcsSectionColumn.value = searchState.pcsSectionColumn;
    selectedProcedureCode.value = searchState.selectedProcedureCode;
  }

  const procedureHistory = useHistoryStore<string>('procedure-history');
  procedureHistory.addToHistory(''); // we add blank as our starting point for history
  const pcsTableCache = new Map<string, CodebookPCSTableResponse>();

  const indexHistory = useHistoryStore<BookViewRecord|SearchViewRecord>('index-history', (one: BookViewRecord|SearchViewRecord, two: BookViewRecord|SearchViewRecord) => {
    return one?.data_id == two?.data_id;
  });

  const bookSelected = ref(CodeBookDropdownOptions[0]);
  const getBookSelected = computed(() => bookSelected.value);
  const getBookCode = computed(() => {

    if (!isEmpty(searchResultDetails.value)) {
      return searchResultDetails.value?.[0]?.data_book;
    }

    return codeBookLookupMap.get(bookSelected.value);
  });
  const isProceduresBookSelected = computed(() => bookSelected.value === 'Procedure, ICD-10-PCS');
  const getBookTitle = computed(() => {
    if (CodeBookDropdownOptions.includes(bookSelected.value)) {
      const bookNameSplit = bookSelected.value.split(', ');
      const bookTitle = bookNameSplit.length > 1 ? bookNameSplit[1] +  ' ' + bookNameSplit[0] : bookSelected.value;
      return bookTitle + (getBookCode.value?.endsWith('TABULAR') ? ' Tabular' : ' Index');
    } else {
      return '';
    }
  });
  const modeSelected = ref<SearchMode>('');
  const isTableModeSelected = computed(() => modeSelected.value == 'table');

  const searchText = ref<string>('');
  const searchResults = ref<SearchViewRecord[] | undefined>([]);
  const selectedSearchResult = ref<SearchViewRecord | undefined> ();
  function setSelectedSearchResult (searchRecord: SearchViewRecord){
    selectedSearchResult.value = searchRecord;
  }
  const searchResultDetails = ref(<BookViewRecord[] | undefined>[]);
  const searchTable = ref<CodebookPCSTableResponse | undefined>();
  const searchHistory = ref<SearchHistory>([]);
  const getSearchHistory = computed(() => searchHistory.value);

  const pcsSectionColumn = ref<Column>();
  const getPcsSectionColumn = computed(() => pcsSectionColumn.value);
  const selectedProcedureCode = ref('');
  const getSelectedProcedureCode = computed(() => selectedProcedureCode.value);

  const subSearchTerm = ref<string>('');
  const searchFocused = ref<boolean>(true);
  const clearSubSearch = ref<boolean>(false);
  const foundTerms = ref<string[]>([]);
  const currentSubSearchIndex = ref<number | undefined>();
  const selectedIndexRecordID = ref<string>('');
  const selectedTabularRecordID = ref<number>(-1);
  const selectedIndexNode = ref<string>('');
  const firstSubSearchPerformed = ref(false);

  async function setSelectedProcedureCode(code: string, addToHistory = true) {

    if (code && (!selectedProcedureCode.value || code[0].toLowerCase() != selectedProcedureCode.value.toLowerCase())) {
      await getPCSTable(code[0]);
    }
    selectedProcedureCode.value = code;

    if (addToHistory) {
      procedureHistory.addToHistory(code);
    }
  }
  const getSelectedProcedureParts = computed(() => {
    const parts: ProcedureColumnItem[] = [];
    if (!selectedProcedureCode.value || !searchTable.value) {
      return parts;
    }

    const section = pcsSectionColumn.value?.choices.find(x => x.char == selectedProcedureCode.value?.[0]);

    if (!section) {
      return parts;
    }

    parts.push(section);

    for (let i=1; i<selectedProcedureCode.value.length; i++) {
      const char = selectedProcedureCode.value[i];
      const part = searchTable.value.columns.find(col => col.index == i)?.choices.find(x => x.char == char);
      if (part) {
        parts.push(part);
      }
    }
    return parts;
  });

  function fetchUpdatedHistory(term?: string) {
    searchService.addSearchHistory(term).then((history) => {
      searchHistory.value = history;
    }).catch((err) => console.error((err)));
  }
  const initialItemRecord = ref<SearchViewRecord>();
  const bookId = ref<string>();

  function setBookSelected(book: string, reset?:boolean) {
    bookSelected.value = book;
    //if procedures book is selected mode should be set to default (index), otherwise mode cannot be set and should be empty
    setModeSelected(isProceduresBookSelected.value ? 'index' : '');
    if(!!reset) {
      resetSearch();
    }
  }

  function setModeSelected(mode: SearchMode) {
    modeSelected.value = mode;
  }

  async function focusIndexItem(term: string, options?: string, addHistory?: boolean) {
    const bookId = codeBookLookupMap.get(bookSelected.value);
    if (bookId) {
      const results = await searchService.getCodebooksSearch(bookId, term, useGrouperStore().patientEffectiveDate, options);
      await loadFirstRecord(results, addHistory);
      await resourcesStore.getResearch(term, bookId);
    }
  }

  async function getCodebookTermSearchAndInitialDetails(term: string, findAll?: boolean): Promise<SearchViewRecord[] | undefined> {
    const bookId = codeBookLookupMap.get(bookSelected.value);
    if (bookId) {
      const searchResults = await getCodebookTermSearch(bookId, term, findAll);

      // store search history async and don't wait for it to finish
      fetchUpdatedHistory(term);
      await loadFirstRecord(searchResults, true);
    }
    return searchResults.value;
  }

  async function loadFirstRecord(list: SearchViewRecord[], addHistory?: boolean) {
    if (!isEmpty(list)) {
      const firstRecord = list[0];
      if (firstRecord) {
        // add to index history
        console.log(firstRecord)
        if(addHistory) {
          indexHistory.addToHistory(firstRecord);
        }
        await getResultDetails(firstRecord);
      }
    }
  }

  async function getResultDetails(result?: SearchViewRecord){
    if (!result) {
      return;
    }

    // clear the selected procedure if there was one
    selectedProcedureCode.value = '';

    initialItemRecord.value = result;
    bookId.value = codeBookLookupMap.get(bookSelected.value);
    if (bookId.value) {
      const code = result?.codes?.length || 0 > 0 ? result?.codes?.[0] : '';
      if (!isProceduresBookSelected.value){
        if ((code && code.endsWith('-'))){
          await focusIndexItem(code);
        } else {
          await getCodebookDetails(result.data_book, result.data_sequence);
        }
      } else {
        const partialCodeArray = result?.text?.match(trucodePartialProcedureCodeLink) || [];
        const partialCode = partialCodeArray.length > 1 ? partialCodeArray[1] : '';
        const procCode = code === '' ? partialCode : code;
        if (procCode) {
          await setSelectedProcedureCode((procCode));
        } else {
          await getCodebookDetails(bookId.value + '_INDEX', result.data_sequence);
        }
      }
    }

    // set book to right book for the results
    if (!isEmpty(searchResultDetails.value)) {
      const resultBook = searchResultDetails?.value?.[0]?.data_book || '';
      const newBook = Array.from(codeBookLookupMap.entries()).find(([key, value]) => resultBook.startsWith(value));

      if (newBook) {
        bookSelected.value = newBook[0];
      }
    }
  }

  async function getCodebookTermSearch(bookId: string,  term: string, findAll?: boolean): Promise<SearchViewRecord[] | []> {

    let options;
    if (findAll) {
      options = FIND_ALL;
    }

    const results =  await searchService.getCodebooksSearch(bookId, term, useGrouperStore().patientEffectiveDate, options)
    searchResults.value = results;

    return results;
  }

  async function getCodebookDetails( bookId: string, sequenceId: number, records = 50): Promise<BookViewRecord[] | undefined> {
    const resultDetails = await searchService.getCodebooksDetails(bookId, sequenceId, useGrouperStore().patientEffectiveDate, records);
    searchResultDetails.value = resultDetails;
    searchTable.value = undefined;

    return resultDetails;
  }

  async function getPCSTable(code: string): Promise<CodebookPCSTableResponse | undefined> {

    let resultTable: CodebookPCSTableResponse | undefined;
    if (pcsTableCache.has(code)) {
      resultTable = pcsTableCache.get(code);
    } else {
      resultTable = await searchService.getCodebooksPCSTable(code, useGrouperStore().patientEffectiveDate);
      if (resultTable) {
        pcsTableCache.set(code, resultTable);
      }
    }

    searchTable.value = resultTable;
    searchResultDetails.value = [];

    return resultTable;
  }

  function resetSearch(){

    searchText.value = '';
    searchResults.value = [];
    selectedSearchResult.value = undefined;
    searchResultDetails.value = [];
    initialItemRecord.value = undefined;
    searchTable.value = undefined;
    pcsSectionColumn.value = undefined;
    selectedProcedureCode.value = '';

    if (bookSelected.value) {
      focusIndexItem('A', undefined, true);
    }
  }

  function changeSearchText(text: string){
    searchText.value = text;
    if (text) {
      getCodebookTermSearchAndInitialDetails(text, false);
    }
  }

  async function addDxOrProcCode(code: string){
    try {
      if (isProceduresBookSelected.value){
        await useGrouperStore().addProcedureCode(code, undefined, true);
      } else {
        await useGrouperStore().addDiagnosisCode(code, undefined, undefined, true);
      }
    } catch {
      //TODO, what should happen if code isn't added?
      console.log('Something went wrong, ' + code + ' was not added');
    }
  }

  async function quickAddCodeSearch(text: string, codeType: CodeType): Promise<DrgAnalysisCode|undefined> {

    const codes = await searchService.searchCodes(text, codeType);

    text = cleanText(text);
    return codes?.find(code => cleanText(code.value) === text);
  }

  function cleanText(text: string): string {
    return text.replace('.', '').toLowerCase().trim();
  }

  async function fetchPcsSections() {
    if (!pcsSectionColumn.value) {
      pcsSectionColumn.value = (await searchService.getCodebookPCSSections(useGrouperStore().patientEffectiveDate))?.columns[0];
    }
  }

  async function subSearch(term: string) {
    if (term) {
      const bookId = codeBookLookupMap.get(bookSelected.value);
      console.log(bookId)
      if (bookId) {
        foundTerms.value = [];
        const results =  await searchService.getCodebooksSearch(bookId, term, useGrouperStore().patientEffectiveDate, '16', '' + selectedIndexRecordID.value)
        currentSubSearchIndex.value = 0;
        foundTerms.value = results.map(item => '' + item.data_id);
        return subSearchTerm.value = foundTerms.value[currentSubSearchIndex.value];
      }
    } else {
      currentSubSearchIndex.value = undefined;
      foundTerms.value = [];
      subSearchTerm.value = '';
    }
  }

  async function subSearchNav(term: string) {
    return subSearchTerm.value = term;
  }

  return {
    getSearchState,
    setSearchState,
    bookSelected,
    getBookSelected,
    getBookCode,
    isProceduresBookSelected,
    getBookTitle,
    modeSelected,
    isTableModeSelected,
    initialItemRecord,
    bookId,
    searchResults,
    selectedSearchResult,
    setSelectedSearchResult,
    changeSearchText,
    searchText,
    searchResultDetails,
    searchTable,
    setBookSelected,
    setModeSelected,
    getCodebookTermSearchAndInitialDetails,
    getResultDetails,
    getCodebookTermSearch,
    getCodebookDetails,
    getPCSTable,
    addDxOrProcCode,
    quickAddCodeSearch,
    fetchUpdatedHistory,
    searchHistory,
    getSearchHistory,
    getSelectedProcedureCode,
    setSelectedProcedureCode,
    fetchPcsSections,
    getPcsSectionColumn,
    getSelectedProcedureParts,
    procedureHistory,
    focusIndexItem,
    indexHistory,
    subSearchNav,
    subSearch,
    subSearchTerm,
    clearSubSearch,
    searchFocused,
    foundTerms,
    currentSubSearchIndex,
    selectedIndexRecordID,
    selectedTabularRecordID,
    selectedIndexNode,
    firstSubSearchPerformed
  }
});

export interface ProcedureColumnItem {
  char: string;
  label: string;
}
